/** Player — paper.io2 grid-based mover with continuous interpolation.
 *  Reference: docs/STAGE_DESIGN.md §1.1 / §1.2 / §1.3
 *  Day 5: 8-directional movement, resetTo for game-over recovery.
 */

import { Container, Graphics } from 'pixi.js';
import type { Direction, GridConfig, GridPos } from './types';
import { DIRECTION_DELTA, directionStepDistance, isReverse } from './types';

export interface PlayerCharacterMeta {
  readonly bodyColor: number;
  readonly accentColor: number;
  readonly inkColor: number;
  readonly creamColor: number;
  readonly handleColor: number;
}

export interface PlayerOptions {
  readonly grid: GridConfig;
  readonly initialPos: GridPos;
  readonly color: number;
  readonly speedCellsPerSec: number;
  /** キャラクター視覚情報。未指定なら従来のゴールド+クリーム(apprentice 相当) */
  readonly characterMeta?: PlayerCharacterMeta;
}

export class Player {
  public readonly view: Container;

  private readonly grid: GridConfig;
  private readonly speedCellsPerSec: number;
  private readonly graphics: Graphics;
  private readonly initialPos: GridPos;

  private gridPos: GridPos;
  private direction: Direction = 'right';
  private nextDirection: Direction = 'right';
  private cellProgress = 0;

  /** 直近の update() で境界外へ進もうとして clamp が発生したか。
   *  GameplayScreen が毎フレーム読み、true なら "Fell off the island!" で handleFailure。 */
  public hitBoundary = false;

  constructor(options: PlayerOptions) {
    this.grid = options.grid;
    this.speedCellsPerSec = options.speedCellsPerSec;
    this.gridPos = options.initialPos;
    this.initialPos = options.initialPos;

    this.view = new Container();
    this.graphics = new Graphics();
    this.drawDragonSilhouette(options.color, options.characterMeta);
    this.view.addChild(this.graphics);

    this.updateRenderPos();
  }

  setDirection(dir: Direction): void {
    if (isReverse(this.direction, dir)) {
      // 即時反転禁止(自トレイル切断防止 — STAGE_DESIGN §1.1)
      return;
    }
    this.nextDirection = dir;
  }

  /** 1 フレーム分位置を進める。新マスに入ったらそのマス座標を返す。
   *  境界外へ進もうとした場合は this.hitBoundary = true をセット(GameplayScreen が検出)。
   *
   *  ★ 斜め移動なめらか化(Phase A2 Day 8):
   *  旧実装は「境界到達時に direction を新方向へ即コミット → その後新方向に gridPos を更新」
   *  していたため、`右 → 右上` の変更時に視覚位置が 1 セル上へテレポートしていた。
   *  正しい順序は「① 古い direction で次セルへ移動を完了 → ② そこで nextDirection をコミット」。
   *  これで境界跨ぎの瞬間の見た目スナップがゼロになり、対角移動が連続的に見える。
   */
  update(deltaSeconds: number): GridPos | null {
    this.hitBoundary = false;
    const stepDistance = directionStepDistance(this.direction);
    this.cellProgress += (deltaSeconds * this.speedCellsPerSec) / stepDistance;

    let entered: GridPos | null = null;
    while (this.cellProgress >= 1.0) {
      this.cellProgress -= 1.0;
      // ① 古い direction で次セルへ移動完了(視覚との連続性を保つ)
      const delta = DIRECTION_DELTA[this.direction];
      const wantX = this.gridPos.x + delta.x;
      const wantY = this.gridPos.y + delta.y;
      const newX = this.clamp(wantX, 0, this.grid.gridSize - 1);
      const newY = this.clamp(wantY, 0, this.grid.gridSize - 1);
      if (wantX !== newX || wantY !== newY) {
        // 端っこへの進入失敗 → 浮島から落ちたフラグ
        this.hitBoundary = true;
      }
      this.gridPos = { x: newX, y: newY };
      entered = this.gridPos;
      // ② 境界を跨いだ「今」次の方向をコミット(次フレーム以降の cellProgress に効く)
      this.direction = this.nextDirection;
    }

    this.updateRenderPos();
    return entered;
  }

  /** Game over 時に呼ぶ。位置を初期に戻し速度蓄積をリセット。 */
  resetTo(pos: GridPos = this.initialPos): void {
    this.gridPos = pos;
    this.cellProgress = 0;
    this.direction = 'right';
    this.nextDirection = 'right';
    this.updateRenderPos();
  }

  getGridPos(): GridPos {
    return this.gridPos;
  }

  getDirection(): Direction {
    return this.direction;
  }

  private updateRenderPos(): void {
    const delta = DIRECTION_DELTA[this.direction];
    const interpolatedX = this.gridPos.x + delta.x * this.cellProgress;
    const interpolatedY = this.gridPos.y + delta.y * this.cellProgress;
    this.view.x = (interpolatedX + 0.5) * this.grid.cellPx;
    this.view.y = (interpolatedY + 0.5) * this.grid.cellPx;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  /** ドラゴンシルエット(楕円胴 + 小耳/角 + 目)。Phase A 範囲のプレースホルダ強化。
   *  +x 方向(右)を進行方向の基準にして描く。回転は GameplayScreen 側で view.rotation を当てる。
   */
  private drawDragonSilhouette(color: number, meta?: PlayerCharacterMeta): void {
    const cp = this.grid.cellPx;
    const g = this.graphics;
    const ink    = meta?.inkColor    ?? 0x39210f;
    const gold   = meta?.accentColor ?? 0xffcf54;
    const cream  = meta?.creamColor  ?? 0xfff1cc;
    const handle = meta?.handleColor ?? 0x8a552b;
    // bodyColor overrides the passed `color` when characterMeta is provided
    const bodyC  = meta?.bodyColor   ?? color;
    const metal = 0xf3eadb;
    const outline = Math.max(1.1, cp * 0.045);

    g.ellipse(0, cp * 0.36, cp * 0.46, cp * 0.12);
    g.fill({ color: 0x000000, alpha: 0.16 });

    g.moveTo(-cp * 0.08, cp * 0.32);
    g.lineTo(cp * 0.56, cp * 0.16);
    g.stroke({ color: handle, width: Math.max(2, cp * 0.095), alpha: 0.92, cap: 'round' });

    g.poly([-cp * 0.4, -cp * 0.06, -cp * 0.72, cp * 0.04, -cp * 0.42, cp * 0.17]);
    g.fill({ color: bodyC, alpha: 0.94 });
    g.poly([-cp * 0.4, -cp * 0.06, -cp * 0.72, cp * 0.04, -cp * 0.42, cp * 0.17]);
    g.stroke({ color: ink, width: outline, alpha: 0.48, join: 'round' });

    g.poly([-cp * 0.12, -cp * 0.08, -cp * 0.34, -cp * 0.36, cp * 0.08, -cp * 0.24]);
    g.fill({ color: bodyC, alpha: 0.62 });
    g.poly([-cp * 0.12, -cp * 0.08, -cp * 0.34, -cp * 0.36, cp * 0.08, -cp * 0.24]);
    g.stroke({ color: ink, width: outline, alpha: 0.32, join: 'round' });

    g.ellipse(-cp * 0.1, cp * 0.05, cp * 0.39, cp * 0.31);
    g.fill({ color: bodyC });
    g.ellipse(-cp * 0.1, cp * 0.05, cp * 0.39, cp * 0.31);
    g.stroke({ color: ink, width: outline, alpha: 0.62 });

    g.ellipse(-cp * 0.03, cp * 0.13, cp * 0.23, cp * 0.13);
    g.fill({ color: cream, alpha: 0.28 });

    const spikeXs = [-0.34, -0.17, 0.01];
    for (const x of spikeXs) {
      g.poly([cp * x, -cp * 0.24, cp * (x + 0.06), -cp * 0.42, cp * (x + 0.14), -cp * 0.23]);
      g.fill({ color: gold, alpha: 0.95 });
      g.poly([cp * x, -cp * 0.24, cp * (x + 0.06), -cp * 0.42, cp * (x + 0.14), -cp * 0.23]);
      g.stroke({ color: ink, width: Math.max(1, cp * 0.025), alpha: 0.35, join: 'round' });
    }

    g.ellipse(cp * 0.28, -cp * 0.04, cp * 0.29, cp * 0.25);
    g.fill({ color: bodyC });
    g.ellipse(cp * 0.28, -cp * 0.04, cp * 0.29, cp * 0.25);
    g.stroke({ color: ink, width: outline, alpha: 0.62 });

    g.poly([cp * 0.15, -cp * 0.22, cp * 0.21, -cp * 0.45, cp * 0.3, -cp * 0.22]);
    g.fill({ color: gold });
    g.poly([cp * 0.31, -cp * 0.22, cp * 0.39, -cp * 0.42, cp * 0.45, -cp * 0.18]);
    g.fill({ color: gold });

    g.ellipse(cp * 0.49, cp * 0.02, cp * 0.13, cp * 0.1);
    g.fill({ color: cream, alpha: 0.34 });

    g.circle(cp * 0.34, -cp * 0.1, cp * 0.115);
    g.fill({ color: 0xffffff, alpha: 0.96 });
    g.circle(cp * 0.34, -cp * 0.1, cp * 0.115);
    g.stroke({ color: ink, width: Math.max(1, cp * 0.025), alpha: 0.45 });
    g.circle(cp * 0.38, -cp * 0.09, cp * 0.055);
    g.fill({ color: ink, alpha: 0.96 });
    g.circle(cp * 0.4, -cp * 0.12, cp * 0.02);
    g.fill({ color: 0xffffff, alpha: 0.9 });

    g.moveTo(cp * 0.43, cp * 0.06);
    g.lineTo(cp * 0.53, cp * 0.07);
    g.stroke({ color: ink, width: Math.max(1, cp * 0.026), alpha: 0.58, cap: 'round' });

    g.circle(cp * 0.21, cp * 0.23, cp * 0.065);
    g.fill({ color: bodyC, alpha: 0.98 });
    g.circle(cp * 0.21, cp * 0.23, cp * 0.065);
    g.stroke({ color: ink, width: Math.max(1, cp * 0.025), alpha: 0.36 });

    g.moveTo(cp * 0.41, cp * 0.2);
    g.lineTo(cp * 0.58, cp * 0.16);
    g.stroke({ color: metal, width: Math.max(2, cp * 0.12), alpha: 0.96, cap: 'round' });
    g.moveTo(cp * 0.42, cp * 0.2);
    g.lineTo(cp * 0.58, cp * 0.16);
    g.stroke({ color: ink, width: Math.max(1, cp * 0.025), alpha: 0.38, cap: 'round' });

    g.poly([cp * 0.56, cp * 0.13, cp * 0.78, cp * 0.08, cp * 0.71, cp * 0.25]);
    g.fill({ color: bodyC, alpha: 0.9 });
    g.poly([cp * 0.56, cp * 0.13, cp * 0.78, cp * 0.08, cp * 0.71, cp * 0.25]);
    g.stroke({ color: ink, width: outline, alpha: 0.42, join: 'round' });
    g.circle(cp * 0.63, cp * 0.14, cp * 0.035);
    g.fill({ color: 0xffffff, alpha: 0.42 });
  }
}
